/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, ErrorText } from '@components/atoms'

import config from '@configs'
import {
	PayPalScriptProvider,
	ReactPayPalScriptOptions,
	PayPalButtons,
} from '@paypal/react-paypal-js'
import { OnApproveData, OnApproveActions } from '@paypal/paypal-js'
import { ResType, paymentApi } from '@api'
import {
	useAppSelector,
	useAppDispatch,
	paymentActions,
	PaymentStateType,
	PaymentDeliveryType,
	CartType,
	PaymentCustomerInfoType,
	PaymentCostType,
	PaymentShippingType,
} from '@redux/index'
import { Validator, ValidatorResultType } from '@validators'
import { Logger } from '@utils'
import { produce } from 'immer'

export const PaypalPayment = () => {
	// redux dispatch
	const reduxDispatch = useAppDispatch()

	// redux state
	const cartList: CartType[] = useAppSelector(state => state.payment.tryToPayment)[
		'cartList'
	]
	const customerId: string = useAppSelector(state => state.customerAuth.customer).id
	const customerInfo: PaymentCustomerInfoType = useAppSelector(
		state => state.payment.paymentCustomerInfo,
	)
	const delivery: PaymentDeliveryType = useAppSelector(
		state => state.payment.paymentDelivery,
	)
	const shipping: PaymentShippingType = useAppSelector(
		state => state.payment.paymentShipping,
	)
	const cost: PaymentCostType = useAppSelector(state => state.payment.paymentCost)
	const errorMessages: PaymentStateType['paymentErrorMessages'] = useAppSelector(
		state => state.payment.paymentErrorMessages,
	)

	// state
	const [paypalErrorMessage, setPaypalErrorMessage] = React.useState('')

	const payPalConfig: ReactPayPalScriptOptions = React.useMemo(() => {
		const countryCode =
			delivery.countryIsoCode === '' ? 'US' : delivery.countryIsoCode.toUpperCase()
		const res = {
			...config.get('paypal'),
			// buyerCountry: countryCode,
			disableFunding: ['card'],
		}
		return res
	}, [delivery])

	const onPaypalCreatingOrder = async () => {
		try {
			const res: ValidatorResultType = Validator.payment({
				// customer
				phoneCountryCode: customerInfo.phoneCountryCode,
				phoneNumber: customerInfo.phoneNumber,

				// delivery
				countryName: delivery.countryName,
				states: delivery.stateName,
				city: delivery.city,
				street1: delivery.street1,
				zip: delivery.zip,

				// shipping
				deliveryCompanyName: shipping.companyName,
			})

			const filterInvalidRes = Object.keys(res).filter(aKey => !res[aKey].isValid)
			const isSuccess = filterInvalidRes.length === 0

			const newErrorMessage = produce(errorMessages, draft => {
				draft.customer.phoneCountryCode = res['phoneCountryCode'].message
				draft.customer.phoneNumber = res['phoneNumber'].message

				draft.delivery.countryName = res['countryName'].message
				draft.delivery.states = res['states'].message
				draft.delivery.city = res['city'].message
				draft.delivery.street1 = res['street1'].message
				draft.delivery.zip = res['zip'].message

				draft.card.cardNumber = ''
				draft.card.expiredMonth = ''
				draft.card.expiredYear = ''
				draft.card.cvc = ''
				draft.card.cardName = ''

				draft.shipping.companyName = res['deliveryCompanyName'].message

				draft.isSuccess = isSuccess
			})

			reduxDispatch(paymentActions.PAYMENT_ERROR_MESSAGES({ ...newErrorMessage }))

			if (newErrorMessage.isSuccess) {
				const body = {
					items: cartList,
					shipping: shipping,
					cost: cost,
				}

				const apiRes: ResType = (await paymentApi.createPaypalOrder({
					body: body,
				})) as ResType

				if (apiRes !== undefined && apiRes?.status === 201) {
					const payPalOrderId: string = apiRes.data[0].payPalOrderId
					return payPalOrderId
				} else {
					setPaypalErrorMessage('Could not initiate PayPal Checkout.')
					throw new Error('')
				}
			} else {
				setPaypalErrorMessage(
					'Some required fields may be missing, or certain inputs may be in an incorrect format.',
				)
				throw new Error('')
			}
		} catch (error) {
			Logger.warn(error)
		}
	}

	const onPaypalApproval = async (data: OnApproveData, actions: OnApproveActions) => {
		try {
			// PayPal에서 승인된 orderID = PayPalOrderId
			const payPalOrderId = data.orderID
			// 2) 서버에 "캡처" API 호출
			//    - 서버에서는 capture → DB 반영까지 모두 처리.
			const apiRes: ResType | undefined = await paymentApi.paypalOrderApproval({
				body: {
					items: cartList,
					customer: {
						phoneCountryCode: customerInfo.phoneCountryCode,
						phoneNumber: customerInfo.phoneNumber,
					},
					delivery: delivery,
					shipping: shipping,
					cost: cost,
					payPalOrderId: payPalOrderId,
					customerId: customerId,
				},
			})

			if (apiRes !== undefined && apiRes.status === 201) {
				onPaymentSuccess()
			} else {
				setPaypalErrorMessage(
					'Please check if any required fields are missing or incorrectly formatted.',
				)
				throw new Error('')
			}
		} catch (error) {
			console.error(error)
			setPaypalErrorMessage(`Sorry, your transaction could not be processed...${error}`)
		}
	}

	const onPaymentSuccess = () => {
		reduxDispatch(
			paymentActions.MODAL_INFO({
				target: 'paymentSuccess',
				title: 'Payment Successful',
			}),
		)
		reduxDispatch(paymentActions.SHOW_MODAL(true))
		setPaypalErrorMessage('')

		// remove cart
		localStorage.setItem('cart', '')
		window.dispatchEvent(new Event('storage'))

		// remove payment data
		reduxDispatch(paymentActions.TRIGGER_RESET_PAYMENT_DATA(true))
	}

	return (
		<View css={style}>
			<PayPalScriptProvider options={payPalConfig} data-sdk-integration-source="button">
				<PayPalButtons
					style={{
						shape: 'rect',
						layout: 'vertical',
						color: 'gold',
						label: 'buynow',
					}}
					createOrder={async () => {
						const res = (await onPaypalCreatingOrder()) ?? ''
						return res
					}}
					onApprove={onPaypalApproval}
					onError={err => {
						console.error('PayPal 결제 오류:', err)
					}}
					// disabled={disabled}
					fundingSource={undefined}
				/>
			</PayPalScriptProvider>
			{paypalErrorMessage !== '' && <ErrorText>{paypalErrorMessage}</ErrorText>}
		</View>
	)
}

const style = css({
	width: 'fit-content',
	minWidth: '30rem',
	minHeight: '20rem',
	float: 'right',
	textAlign: 'left',
	wordBreak: 'break-word',

	'@media (max-width: 600px)': {
		minWidth: '0px',
		width: 'calc(100vw - 4rem)',
	},
})
